<template>
  <div>
    <transition name="mode-fade" mode="out-in">
      <div class="question-box" v-if="!zipcodeSelection">
        <div class="question-holder">
          <h5 class="question-label">{{ label }}</h5>
          <div class="addressFieldWrapper">
            <input
              type="text"
              tabindex="1"
              id="autocomplete"
              maxlength="80"
              autocomplete="off"
              ref="addressField"
              :placeholder="placeHolder"
              v-model="addressValue"
              :class="{ 'form-control fieldHeight': true, 'is-invalid': error }"
              @input="getPredictions"
              v-on:keyup.enter="validateAddress"
              v-on:keyup.esc="open = false"
              v-on:keyup.down.prevent="nextItem"
              v-on:keyup.up.prevent="prevItem"
            />
            <div v-if="error" class="invalid-feedback">
              {{ errMsg }}
            </div>
            <div id="placesResults" class="list-group" v-show="open">
              <button
                class="list-group-item list-group-item-action"
                v-for="(result, index) in results"
                v-bind:key="result.place_id"
                :class="{ active: index == selectedIndex }"
                @click="itemClicked(index)"
              >
                {{ result.description }}
              </button>
            </div>
          </div>
        </div>
        <div class="button-holder">
          <button
            @click="validateAddress"
            class="btn customBtn"
            :disabled="loading"
          >
            {{ buttonLabel }} <i class="bi bi-arrow-right-circle"></i>
          </button>
        </div>
      </div>
      <CustomCard
        v-if="zipcodeSelection"
        key="zipCode"
        type="zipcode"
        :buttonLabel="buttonLabel"
        :buttonIcon="true"
        :label="zipLabel"
        :placeHolder="zipPlaceHolder"
        :errMsg="zipErrMsg"
        :value="zipcode"
        :required="true"
        @submit="submitZip($event)"
      />
    </transition>
  </div>
</template>

<script>
import CustomCard from "./CustomCard.vue";
import { debounce } from "debounce";

export default {
  name: "AddressComponent",
  components: { 
    CustomCard 
  },
  props: [
    "value", 
    "buttonLabel",
    "label",
    "placeHolder",
    "errMsg",
    "zipLabel",
    "zipPlaceHolder",
    "zipErrMsg"
  ],
  data() {
    return {
      addressValue:
        this.value && this.value.fullAddress ? this.value.fullAddress : "",
      error: false,
      open: false,
      results: [],
      zipError: false,
      zipcodeSelection: false,
      googleService: null,
      selectedIndex: -1,
      zipcode: null,
      loading: false,
      finalAddress: {
        fullAddress:
          this.value && this.value.fullAddress ? this.value.fullAddress : "",
        streetNo: this.value && this.value.streetNo ? this.value.streetNo : "",
        streetName:
          this.value && this.value.streetName ? this.value.streetName : "",
        city: this.value && this.value.city ? this.value.city : "",
        state: this.value && this.value.state ? this.value.state : "",
        zipcode: this.value && this.value.zipcode ? this.value.zipcode : ""
      },
      autocomplete: null,
    };
  },
  mounted() {
    this.$loadGoogleMapsApi().then(() => {
      this.googleService = new window.google.maps.places.AutocompleteService();
      this.placesService = new window.google.maps.places.PlacesService(
        document.getElementById("placesResults")
      );
    });
  },
  methods: {
    nextItem: function () {
      if (
        this.results.length > 0 &&
        this.selectedIndex < this.results.length - 1
      ) {
        this.selectedIndex++;
      }
    },
    prevItem: function () {
      if (this.results.length > 0 && this.selectedIndex > -1) {
        this.selectedIndex--;
      }
    },
    selectPlace: function (placeId) {
      this.getPlace(placeId)
        .then((res) => {
          this.addressFill(res.address_components);
        })
        .catch((err) => {
          console.log(err);
        });
    },
    getPredictions: debounce(function (event) {
      this.error = false;
      if (event.target.value) {
        this.googleService.getPlacePredictions(
          {
            input: event.target.value,
            componentRestrictions: { country: "us" },
            type: "address"
          },
          (predictions) => {
            this.results = predictions;
            this.open = true;
          }
        );
      } else {
        this.resetResults();
      }
    }, 350),
    itemClicked: function (index) {
      this.open = false;
      this.selectedIndex = index;
      this.submitAddress(this.results[this.selectedIndex]);
    },
    submitAddress: async function(value) {
      try {
        this.loading = true;
        let result = await this.getPlace(value.place_id);
        this.addressFill(result.address_components);
        this.addressValue = value.description;
        this.finalAddress.fullAddress = value.description;
        this.resetResults();
        this.$emit("submit", this.finalAddress);
      } catch(err) {
        this.loading = false;
      }
    },
    resetResults: function () {
      this.results = [];
      this.selectedIndex = -1;
      this.open = false;
    },
    getPlace: function (placeId) {
      return new Promise((resolve, reject) => {
        this.placesService.getDetails(
          {
            placeId,
            fields: ['address_components']
          },
          (result, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK) {
              resolve(result);
            } else {
              reject(status);
            }
          }
        );
      });
    },
    placesTextSearch: async function (query,type) {
      return new Promise((resolve, reject) => {
        this.placesService.textSearch(
          {
            query,
            type
          },
          (result, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK) {
              resolve(result);
            } else {
              reject(status);
            }
          }
        );
      });
    },
    validateAddress: async function () {
      if (this.selectedIndex > -1) {
        this.submitAddress(this.results[this.selectedIndex]);
      } else {
        if (!this.addressValue) {
          this.error = true;
          return;
        }
        try {
          this.loading = true;
          let correctedAddress = await this.placesTextSearch(this.addressValue, 'street_address');
          this.submitAddress(correctedAddress[0]);
        } catch(err) {
          this.loading = false;
          // show zipcode question
          this.open = false;
          this.zipcodeSelection = true;
        }
      }
    },
    addressFill: function (addressComponents) {
      if (addressComponents) {
        for (let component of addressComponents) {
          let componentType = component.types[0];
          switch (componentType) {
            case "street_number":
              this.finalAddress.streetNo = component.long_name;
              break;

            case "route":
              this.finalAddress.streetName = component.long_name;
              break;

            case "neighborhood":
            case "locality":
              this.finalAddress.city = component.long_name;
              break;

            case "administrative_area_level_1":
              this.finalAddress.state = component.short_name;
              break;

            case "postal_code":
              this.finalAddress.zipcode = component.long_name;
              break;
          }
        }
      }
    },
    submitZip: async function (selectedZipcode) {
      try {
        this.loading = true;
        let zipResult = await this.placesTextSearch(selectedZipcode,'postal_code')
        let result = await this.getPlace(zipResult[0].place_id);
        this.addressFill(result.address_components);
        this.finalAddress.streetName = this.addressValue;
        this.finalAddress.fullAddress = this.addressValue;
        this.resetResults();
        this.$emit("submit", this.finalAddress);
      } catch(err) {
        this.loading = true;
        this.zipError = true;
      }
    }
  }
};
</script>